import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProductCard from '../components/Marketplace/ProductCard';
import CartDrawer from '../components/Marketplace/CartDrawer';
import BulkOrderModal from '../components/Modals/BulkOrderModal';
import { mockProducts } from '../data/mockData';
import { useEcommerce } from '../context/EcommerceContext';
import { writeFetch } from '../api';
import { getCached, getOrFetchCached, invalidateCache } from '../utils/apiCache';

const {
  FiShoppingBag, FiSearch, FiTruck, FiInfo, FiLayers,
  FiChevronLeft, FiChevronRight, FiRefreshCw, FiAlertCircle,
} = FiIcons;

const PRODUCTS_PER_PAGE = 12;
const CATALOG_LIMIT = 100;
const CACHE_KEY = 'marketplace_products';
const CACHE_TTL = 120000;

const ProductSkeleton = () => (
  <div className="bg-white rounded-[40px] overflow-hidden border border-brand-dark flex flex-col h-full">
    <div className="h-64 bg-brand-light animate-pulse" />
    <div className="p-8 flex-grow flex flex-col">
      <div className="h-3 w-16 bg-brand-light rounded animate-pulse mb-3" />
      <div className="h-6 w-3/4 bg-brand-light rounded animate-pulse mb-4" />
      <div className="h-4 w-full bg-brand-light rounded animate-pulse mb-2" />
      <div className="h-4 w-2/3 bg-brand-light rounded animate-pulse mb-6" />
      <div className="mt-auto pt-6 border-t border-brand-light flex items-center justify-between">
        <div className="h-7 w-20 bg-brand-light rounded animate-pulse" />
        <div className="h-10 w-24 bg-brand-light rounded-xl animate-pulse" />
      </div>
    </div>
  </div>
);

const Pagination = ({ currentPage, totalPages, onChange, totalItems, pageSize }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
      <p className="text-xs text-text-muted font-light">
        Showing <span className="font-bold text-accent-navy">{start}</span>–<span className="font-bold text-accent-navy">{end}</span> of <span className="font-bold text-accent-navy">{totalItems}</span>
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2.5 rounded-xl border border-brand-dark bg-white text-accent-navy hover:border-accent-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <button
            key={page}
            onClick={() => onChange(page)}
            className={`w-10 h-10 rounded-xl text-xs font-black transition-all ${
              page === currentPage
                ? 'bg-accent-navy text-white shadow-md'
                : 'bg-white text-text-muted border border-brand-dark hover:border-accent-gold'
            }`}
          >
            {page}
          </button>
        ))}
        <button
          onClick={() => onChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2.5 rounded-xl border border-brand-dark bg-white text-accent-navy hover:border-accent-gold disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Marketplace = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isBulkOrderOpen, setIsBulkOrderOpen] = useState(false);
  const [merchantProducts, setMerchantProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const refreshInProgress = useRef(false);

  const { cart } = useEcommerce();

  const fetchApprovedProducts = async (showLoading = false, forceRefresh = false) => {
    if (refreshInProgress.current) return;
    refreshInProgress.current = true;
    if (showLoading) setLoadingProducts(true);
    setError(null);

    if (forceRefresh) invalidateCache(CACHE_KEY);

    try {
      const approved = await getOrFetchCached(CACHE_KEY, CACHE_TTL, async () => {
        const res = await writeFetch(
          `read/merchant_products_1776540001?sort=created_at&order=desc&page=1&limit=${CATALOG_LIMIT}`
        );
        const response = await res.json();

        if (response?.status !== 'success' || !Array.isArray(response?.data)) {
          throw new Error(response?.message || 'Unable to load products.');
        }

        const allProducts = [...response.data];

        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key || !key.startsWith('mock_products_')) continue;

            const parsed = JSON.parse(localStorage.getItem(key) || 'null');
            if (!Array.isArray(parsed)) continue;

            for (const product of parsed) {
              if (!allProducts.some(item => String(item.id) === String(product.id))) {
                allProducts.push(product);
              }
            }
          }
        } catch (storageError) {
          console.warn('Unable to read locally stored marketplace products', storageError);
        }

        return allProducts
          .filter(product => product?.approval_status === 'approved' && product?.is_active !== 0)
          .map(product => ({
            id: product.id,
            name: product.name ?? '',
            price: parseFloat(product.price) || 0,
            category: product.category ?? '',
            image: product.image_url || 'https://images.unsplash.com/photo-1563241527-3004b7be0ffd?w=800',
            description: product.description ?? '',
            stock: product.stock_count || 0,
            vendor: 'Verified Merchant',
            rating: 5.0
          }));
      });

      setMerchantProducts(approved);
    } catch (err) {
      console.error('Failed to fetch merchant products', err);
      setError('Unable to load products. Please try again.');
    } finally {
      refreshInProgress.current = false;
      if (showLoading) setLoadingProducts(false);
    }
  };

  useEffect(() => {
    const cached = getCached(CACHE_KEY, CACHE_TTL);
    if (cached !== null) {
      setMerchantProducts(cached);
      setLoadingProducts(false);
    } else {
      fetchApprovedProducts(true);
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible' && getCached(CACHE_KEY, CACHE_TTL) === null) {
        fetchApprovedProducts(false);
      }
    };
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, searchTerm]);

  const categories = ['All', 'Flowers', 'Urns', 'Services', 'Accessories'];

  const combinedProducts = useMemo(() => {
    const merchantIds = new Set(merchantProducts.map(product => String(product.id)));
    const uniqueMockProducts = mockProducts.filter(product => !merchantIds.has(String(product.id)));
    return [...merchantProducts, ...uniqueMockProducts];
  }, [merchantProducts]);

  const filteredProducts = useMemo(() => {
    return combinedProducts.filter(product => {
      const matchesCategory = activeCategory === 'All' || product.category === activeCategory;
      const matchesSearch = (product.name ?? '').toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchTerm, combinedProducts]);

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  const isInitialLoading = loadingProducts && merchantProducts.length === 0;

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-4 sm:px-6">
        <section className="max-w-4xl mx-auto text-center mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center gap-2 bg-accent-gold/10 text-accent-gold px-4 py-1.5 md:px-5 md:py-2 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] mb-6 md:mb-8 border border-accent-gold/20"
          >
            <SafeIcon icon={FiShoppingBag} /> Legacy Marketplace
          </motion.div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-accent-navy mb-4 md:mb-8 leading-tight">
            Sympathy & Remembrance
          </h1>
          <p className="text-lg md:text-xl text-text-muted font-light max-w-2xl mx-auto leading-relaxed italic px-2">
            "Carefully curated arrangements and memorial tokens to help you honor a life well-lived."
          </p>
        </section>

        <div className="sticky top-[72px] md:top-24 z-40 bg-brand-light/90 backdrop-blur-md py-4 md:py-6 -mx-4 px-4 md:-mx-6 md:px-6 mb-8 md:mb-12 border-b border-brand-dark/30 flex flex-col md:flex-row gap-4 md:gap-6 items-center">
          <div className="relative w-full md:max-w-md group">
            <SafeIcon icon={FiSearch} className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-accent-gold w-5 h-5" />
            <input
              className="w-full pl-12 md:pl-14 pr-4 md:pr-6 py-3 md:py-4 bg-white border border-brand-dark rounded-xl md:rounded-2xl outline-none focus:border-accent-gold transition-all text-sm md:text-base"
              placeholder="Search memorial products..."
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
            />
          </div>

          <div className="w-full md:w-auto flex-grow flex justify-start md:justify-center gap-2 overflow-x-auto hide-scrollbar pb-2 md:pb-0">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 md:px-8 py-2.5 md:py-3 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all border-2 shrink-0 ${activeCategory === category ? 'bg-accent-navy border-accent-navy text-white' : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'}`}
              >
                {category}
              </button>
            ))}
          </div>

          <button
            onClick={() => setIsCartOpen(true)}
            className="absolute md:relative right-4 md:right-auto top-4 md:top-auto p-3 md:p-4 bg-white border border-brand-dark rounded-xl md:rounded-2xl text-accent-navy hover:border-accent-gold transition-all group"
          >
            <SafeIcon icon={FiShoppingBag} className="w-5 h-5 md:w-6 md:h-6" />
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 md:w-6 md:h-6 bg-accent-gold text-white text-[9px] md:text-[10px] font-black rounded-full flex items-center justify-center shadow-lg animate-bounce">
                {cart.length}
              </span>
            )}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="lg:w-1/4 space-y-6 md:space-y-8 order-2 lg:order-1">
            <div className="bg-white p-6 md:p-8 rounded-[32px] md:rounded-[40px] border border-brand-dark shadow-sm">
              <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-4 md:mb-6 flex items-center gap-2">
                <SafeIcon icon={FiTruck} className="text-accent-gold" /> Direct Delivery
              </h4>
              <p className="text-xs text-text-muted leading-relaxed font-light mb-6">
                We coordinate directly with funeral homes and memorial sites globally to ensure your tokens arrive with dignity and on schedule.
              </p>
              <div className="p-4 bg-brand-light rounded-2xl border border-brand-dark/50 flex items-start gap-3">
                <SafeIcon icon={FiInfo} className="text-accent-gold mt-1 shrink-0" />
                <p className="text-[10px] font-bold text-accent-navy leading-tight">
                  Same-day delivery available for floral arrangements in major metropolitan areas.
                </p>
              </div>
            </div>

            <div className="bg-accent-navy text-white p-6 md:p-8 rounded-[32px] md:rounded-[40px] shadow-xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-accent-gold/10 rounded-full -mr-12 -mt-12 blur-2xl" />
              <h4 className="font-serif text-lg md:text-xl mb-3 md:mb-4 relative z-10">Bulk Orders</h4>
              <p className="text-[11px] md:text-xs text-white/60 font-light leading-relaxed mb-6 relative z-10">
                Planning a large memorial? We offer volume pricing for silk scarves, memorial programs, and candle sets.
              </p>
              <button
                onClick={() => setIsBulkOrderOpen(true)}
                className="w-full py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all relative z-10"
              >
                Request Quote
              </button>
            </div>
          </div>

          <div className="lg:w-3/4 order-1 lg:order-2">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 flex items-center justify-between gap-4 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
              >
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
                <button
                  onClick={() => fetchApprovedProducts(true, true)}
                  className="flex items-center gap-1.5 font-bold underline hover:no-underline transition-all"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" /> Retry
                </button>
              </motion.div>
            )}

            {loadingProducts && merchantProducts.length > 0 && (
              <div role="status" className="mb-5 flex items-center gap-3 rounded-2xl border border-brand-dark bg-white px-5 py-3 text-sm text-text-muted">
                <span className="w-4 h-4 border-2 border-accent-gold/30 border-t-accent-gold rounded-full animate-spin" />
                Refreshing products…
              </div>
            )}

            <AnimatePresence mode="popLayout">
              {isInitialLoading ? (
                <motion.div
                  key="skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8"
                >
                  {Array.from({ length: PRODUCTS_PER_PAGE }).map((_, index) => (
                    <ProductSkeleton key={index} />
                  ))}
                </motion.div>
              ) : paginatedProducts.length > 0 ? (
                <motion.div
                  key="grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8"
                >
                  {paginatedProducts.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20 md:py-32 bg-white rounded-[32px] md:rounded-[50px] border-2 border-dashed border-brand-dark px-4"
                >
                  <SafeIcon icon={FiLayers} className="w-16 h-16 md:w-20 md:h-20 text-text-muted/10 mx-auto mb-4 md:mb-6" />
                  <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-2">No items found</h3>
                  <p className="text-sm text-text-muted font-light max-w-sm mx-auto">
                    Try adjusting your filters or search terms to find the right memorial token.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={filteredProducts.length}
              pageSize={PRODUCTS_PER_PAGE}
              onChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <BulkOrderModal isOpen={isBulkOrderOpen} onClose={() => setIsBulkOrderOpen(false)} />
    </div>
  );
};

export default Marketplace;